import { supabase } from '../supabaseClient';

// Tables expected:
// - downloads(id, created_at, title, subtitle, file_bucket, file_path, file_url)
// - announcements(id, created_at, title, subtitle, file_bucket, file_path, file_url)
// - site_config(key, value_json)
//
// file_url is optional (lets you link to Google Drive etc). If not provided,
// we build a public URL from (file_bucket, file_path).

function buildPublicUrl(bucket, path) {
  if (!bucket || !path) return '';
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data?.publicUrl || '';
}

function normalizePayload(payload) {
  // Prevent accidental empty strings
  const p = { ...payload };
  if ('subtitle' in p && (p.subtitle === '' || p.subtitle === undefined)) p.subtitle = null;
  if ('file_url' in p && (p.file_url === '' || p.file_url === undefined)) p.file_url = null;
  if ('file_bucket' in p && (p.file_bucket === '' || p.file_bucket === undefined)) p.file_bucket = null;
  if ('file_path' in p && (p.file_path === '' || p.file_path === undefined)) p.file_path = null;
  return p;
}

// ---------------------
// Site config (singletons like Brochure, etc)
// ---------------------
export async function getSiteConfig(key) {
  const { data, error } = await supabase.from('site_config').select('*').eq('key', key).maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function setSiteConfig(key, valueJson) {
  const payload = { key, value_json: valueJson };
  const { data, error } = await supabase.from('site_config').upsert(payload).select().single();
  if (error) throw error;
  return data;
}

export async function getBrochureUrl() {
  const row = await getSiteConfig('brochure');
  const v = row?.value_json || null;
  if (!v) return '';
  return v.file_url || buildPublicUrl(v.file_bucket, v.file_path);
}

export async function setBrochure({ file_url = null, file_bucket = null, file_path = null } = {}) {
  return setSiteConfig('brochure', { file_url, file_bucket, file_path });
}

export async function listDownloads() {
  const { data, error } = await supabase
    .from('downloads')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;

  return (data || []).map((row) => ({
    ...row,
    url: row.file_url || buildPublicUrl(row.file_bucket, row.file_path),
    kind: row.file_url ? 'link' : 'file',
  }));
}

export async function listAnnouncements() {
  const { data, error } = await supabase
    .from('announcements')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;

  return (data || []).map((row) => ({
    ...row,
    url: row.file_url || buildPublicUrl(row.file_bucket, row.file_path),
    kind: row.file_url ? 'link' : 'file',
  }));
}

// ---------------------
// Downloads CRUD
// ---------------------
export async function createDownload(payload) {
  const p = normalizePayload(payload);
  const { data, error } = await supabase.from('downloads').insert(p).select().single();
  if (error) throw error;
  return data;
}

export async function updateDownload(id, payload) {
  if (!id) throw new Error('Missing id for update');
  const p = normalizePayload(payload);
  const { data, error } = await supabase.from('downloads').update(p).eq('id', id).select().single();
  if (error) throw error;
  return data;
}

export async function deleteDownload(id) {
  if (!id) throw new Error('Missing id for delete');
  const { error } = await supabase.from('downloads').delete().eq('id', id);
  if (error) throw error;
  return true;
}

// ---------------------
// Announcements CRUD
// ---------------------
export async function createAnnouncement(payload) {
  const p = normalizePayload(payload);
  const { data, error } = await supabase.from('announcements').insert(p).select().single();
  if (error) throw error;
  return data;
}

export async function updateAnnouncement(id, payload) {
  if (!id) throw new Error('Missing id for update');
  const p = normalizePayload(payload);
  const { data, error } = await supabase
    .from('announcements')
    .update(p)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteAnnouncement(id) {
  if (!id) throw new Error('Missing id for delete');
  const { error } = await supabase.from('announcements').delete().eq('id', id);
  if (error) throw error;
  return true;
}

// Backwards compatibility with older Admin code (if any)
export async function upsertDownload(payload) {
  // Prefer explicit functions; keep this so nothing else breaks.
  if (payload?.id) {
    const { id, ...rest } = payload;
    return updateDownload(id, rest);
  }
  return createDownload(payload);
}

export async function upsertAnnouncement(payload) {
  if (payload?.id) {
    const { id, ...rest } = payload;
    return updateAnnouncement(id, rest);
  }
  return createAnnouncement(payload);
}
